import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Globe,
  Pencil,
  Pin,
  RotateCcw,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/ui/components/common/Badge'
import { FactScoreInline } from '@/ui/components/common/FactScoreInline'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { markdownContainsImage } from '@/ui/utils/markdown'
import { EditFactDialog } from '@/ui/components/facts/EditFactDialog'
import {
  useDeleteFact,
  useGetAllFactsByLead,
  usePublishFact,
  useResetFactVotes,
  useUnpublishFact,
} from '@/application/services/fact.service'
import { useSetActiveFact } from '@/application/services/investigation.service'
import type { Fact } from '@/domains/types'
import { classNames } from '@/ui/utils/main'

const LIVE_REFETCH_INTERVAL = 3 * 1000
const DESCRIPTION_THRESHOLD = 150

const FACT_CARD_LINK_CLASS = 'no-underline hover:no-underline block h-full'

/** Wraps a fact card in an external link only when the fact has a source URL. */
function FactCardLink({ url, children }: { url: string | null; children: React.ReactNode }) {
  if (!url) return <div className={FACT_CARD_LINK_CLASS}>{children}</div>
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={FACT_CARD_LINK_CLASS}>
      {children}
    </a>
  )
}

/**
 * Wrapping gallery of facts attached to a single lead.
 * Fetches ALL facts (published + unpublished) and auto-refreshes every 10 s
 * so new Twitch-submitted facts appear without a page reload.
 * Lets the streamer publish, delete, pin as active fact, or edit a fact.
 */
export function LeadFacts({
  leadId,
  investigationId,
  activeFactId,
  activeLeadId,
  isActiveInvestigation,
}: {
  leadId: string
  investigationId: string
  activeFactId?: string | null
  activeLeadId?: string | null
  isActiveInvestigation: boolean
}) {
  const { t } = useTranslation()

  const { data: facts, isLoading } = useGetAllFactsByLead(leadId, {
    refetchInterval: LIVE_REFETCH_INTERVAL,
  })

  // Sort facts: original source always first, then active fact, then the rest
  const sortedFacts = useMemo(() => {
    if (!facts) return []
    const originalSource = facts.filter((fact) => fact.originalSource)
    const rest = facts.filter((fact) => !fact.originalSource)
    const sortedRest = activeFactId
      ? [
          ...rest.filter((fact) => fact.id === activeFactId),
          ...rest.filter((fact) => fact.id !== activeFactId),
        ]
      : rest
    return [...originalSource, ...sortedRest]
  }, [facts, activeFactId])
  const { mutateAsync: publishFact } = usePublishFact(leadId, investigationId)
  const { mutateAsync: unpublishFact } = useUnpublishFact(leadId, investigationId)
  const { mutateAsync: deleteFact } = useDeleteFact(leadId)
  const { mutateAsync: resetFactVotes } = useResetFactVotes(leadId)
  const { mutateAsync: setActiveFact } = useSetActiveFact()

  const [expandedFacts, setExpandedFacts] = useState<Set<string>>(new Set())

  const toggleFactExpanded = (factId: string) => {
    setExpandedFacts((prev) => {
      const next = new Set(prev)
      if (next.has(factId)) {
        next.delete(factId)
      } else {
        next.add(factId)
      }
      return next
    })
  }

  const [editFact, setEditFact] = useState<Fact | null>(null)

  const handlePublish = async (fact: Fact) => {
    try {
      await publishFact(fact.id)
      toast.success(t('lead_detail.fact_publish_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_publish_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleUnpublish = async (fact: Fact) => {
    try {
      await unpublishFact(fact.id)
      toast.success(t('lead_detail.fact_unpublish_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_unpublish_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleDelete = async (fact: Fact) => {
    if (!window.confirm(t('lead_detail.fact_delete_confirm'))) return
    try {
      await deleteFact(fact.id)
      toast.success(t('lead_detail.fact_deleted'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_delete_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleResetVotes = async (factId: string) => {
    if (!window.confirm(t('lead_detail.fact_reset_votes_confirm'))) return
    try {
      await resetFactVotes(factId)
      toast.success(t('lead_detail.fact_reset_votes_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_reset_votes_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleSetActiveFact = async (fact: Fact) => {
    const nextFactId = activeFactId === fact.id ? null : fact.id
    try {
      await setActiveFact({ investigationId, factId: nextFactId })
      toast.success(
        nextFactId ? t('admin.live_active_fact_set') : t('admin.live_active_fact_cleared'),
      )
    } catch (_error: unknown) {
      toast.error(t('admin.live_active_fact_failed'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: 2 }).map((_item, index) => (
          <Skeleton key={index} className="h-24 w-48 rounded-lg" />
        ))}
      </div>
    )
  }

  if (!facts || facts.length === 0) {
    return <p className="text-xs text-muted-foreground italic">{t('lead_detail.no_facts')}</p>
  }

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {sortedFacts.map((fact) => (
          <div key={fact.id} className="relative group min-w-48">
            {/* Card — clicking the body opens the external URL, when there is one */}
            <FactCardLink url={fact.url}>
              <div
                className={`bg-card border rounded-lg overflow-hidden hover:shadow-sm transition-all h-full flex ${
                  fact.published
                    ? 'border-border hover:border-primary/50'
                    : 'border-warning/40 hover:border-warning/70'
                }`}
              >
                {fact.imageUrl && (
                  <div className="max-w-48 min-h-48 self-stretch shrink-0 overflow-hidden">
                    <img
                      src={fact.imageUrl}
                      alt={fact.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-4 flex-1 min-w-30 max-w-lg flex flex-col">
                  <div className="flex items-start justify-between gap-1.5 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs font-semibold line-clamp-2 text-foreground leading-snug">
                        {fact.title}
                      </span>
                      {fact.originalSource && (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-info/10 text-info border-info/20 px-1 py-0 shrink-0"
                        >
                          {t('admin.live_original_source_badge')}
                        </Badge>
                      )}
                      {activeFactId === fact.id && (
                        // Intentionally left as a literal purple accent: this is a
                        // one-off "pinned/highlighted" semantic distinct from
                        // success/warning/info/destructive, not worth a 5th token
                        // for a single badge.
                        <Badge
                          variant="secondary"
                          className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/20 px-1 py-0 shrink-0"
                        >
                          {t('admin.live_active_fact_badge')}
                        </Badge>
                      )}
                      {fact.published ? (
                        <Badge
                          variant="secondary"
                          className="text-xs text-success border-success/30 bg-success/10"
                        >
                          {t('lead_detail.fact_published')}
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-xs text-warning border-warning/30 bg-warning/10"
                        >
                          {t('lead_detail.fact_unpublished')}
                        </Badge>
                      )}
                    </div>
                    {fact.url && <ExternalLink className="w-3 h-3 text-primary shrink-0 mt-0.5" />}
                  </div>
                  {fact.description && (
                    <div className="mt-1">
                      <MarkdownView
                        content={fact.description}
                        compact={!expandedFacts.has(fact.id)}
                        className={classNames(
                          'text-xs text-muted-foreground leading-snug',
                          !expandedFacts.has(fact.id) && 'max-h-24 overflow-hidden',
                        )}
                      />
                      {(fact.description.length > DESCRIPTION_THRESHOLD ||
                        markdownContainsImage(fact.description)) && (
                        <div className="flex justify-end mt-1">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              toggleFactExpanded(fact.id)
                            }}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {expandedFacts.has(fact.id)
                              ? t('lead_detail.fact_see_less')
                              : t('lead_detail.fact_see_more')}
                            {expandedFacts.has(fact.id) ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-end justify-between mt-auto pt-2 gap-2">
                    {(fact.submittedByTwitchUsername ?? fact.submittedByUsername) ? (
                      <span className="text-xs text-muted-foreground truncate">
                        {t('common.submitted_by')}:{' '}
                        <span className="text-foreground/80">
                          {fact.submittedByTwitchUsername ?? fact.submittedByUsername}
                        </span>
                      </span>
                    ) : (
                      <span />
                    )}
                    <FactScoreInline
                      leadId={leadId}
                      factId={fact.id}
                      showBoth
                      refetchInterval={LIVE_REFETCH_INTERVAL}
                    />
                  </div>
                </div>
              </div>
            </FactCardLink>

            {/* Action buttons — appear on hover, overlay the card */}
            <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              {isActiveInvestigation && activeLeadId === leadId && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    void handleSetActiveFact(fact)
                  }}
                  title={
                    activeFactId === fact.id
                      ? t('admin.live_unset_active_fact')
                      : t('admin.live_set_active_fact')
                  }
                  className={`p-1 rounded bg-background/90 border border-border transition-all ${
                    activeFactId === fact.id
                      ? 'text-purple-600 bg-purple-500/10'
                      : 'text-muted-foreground hover:text-purple-600 hover:bg-purple-500/10'
                  }`}
                >
                  <Pin className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setEditFact(fact)
                }}
                title={t('lead_detail.fact_edit_title')}
                className="p-1 rounded bg-background/90 border border-border text-muted-foreground hover:bg-muted/50 transition-all"
              >
                <Pencil className="w-3 h-3" />
              </button>
              {!fact.published && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    void handlePublish(fact)
                  }}
                  title={t('lead_detail.fact_publish_button')}
                  className="p-1 rounded bg-background/90 border border-border text-success hover:bg-success/10 transition-all"
                >
                  <Globe className="w-3 h-3" />
                </button>
              )}
              {fact.published && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    void handleUnpublish(fact)
                  }}
                  title={t('lead_detail.fact_unpublish_button')}
                  className="p-1 rounded bg-background/90 border border-border text-warning hover:bg-warning/10 transition-all"
                >
                  <Globe className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  void handleResetVotes(fact.id)
                }}
                title={t('lead_detail.fact_reset_votes')}
                className="p-1 rounded bg-background/90 border border-border text-warning hover:bg-warning/10 transition-all"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  void handleDelete(fact)
                }}
                title={t('common.delete')}
                className="p-1 rounded bg-background/90 border border-border text-destructive hover:bg-destructive/10 transition-all"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <EditFactDialog
        fact={editFact}
        leadId={leadId}
        open={editFact !== null}
        onOpenChange={(open) => {
          if (!open) setEditFact(null)
        }}
      />
    </>
  )
}
